import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Scrollbar, useResponsive } from '@zeta-core';
import { ZetaLogo } from '@zeta-core/assets';

import { NAV } from '../config-layout';

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
};

const APP_STORE_URL = 'https://apps.apple.com/gb/app/zeta-fa/id6754025168';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.zeta.fa';

export default function NavVertical({ openNav }: Props) {
  const theme = useTheme();
  const lgUp = useResponsive('up', 'lg');

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #000 0%, #5F3D9E 252.22%)'
              : `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${alpha(theme.palette.primary.light, 0.22)} 100%)`,
          height: '100vh',
          width: '100%',
        }}
      >
        <Box
          marginY={5}
          marginLeft={5}
          paddingY={7}
          borderRadius={2.5}
          height="calc(100vh - 1 * 40px)"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              position: 'absolute',
              top: 40,
              left: 40,
            }}
          >
            <ZetaLogo />
          </Box>

          {/* Content */}
          <Stack spacing={5} alignItems="center" textAlign="center">
            {/* Headline */}
            <Stack spacing={1}>
              <Typography
                sx={{
                  color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                Get the Zeta App
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                  fontSize: 14,
                }}
              >
                Access your wealth anywhere, anytime
              </Typography>
            </Stack>

            {/* QR Codes */}
            <Stack direction="row" spacing={4}>
              <Stack spacing={1} alignItems="center">
                <Box
                  component="a"
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ lineHeight: 0 }}
                >
                  <Box
                    component="img"
                    src="/zeta_appstore.jpg"
                    alt="Download on the App Store"
                    sx={{
                      width: 130,
                      height: 130,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.background.paper, 0.98),
                      p: 1,
                      border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.3 : 1)}`,
                      boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.25)' : '0 8px 24px rgba(15,23,42,0.08)',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.75)' : theme.palette.text.secondary,
                  }}
                >
                  App Store
                </Typography>
              </Stack>

              <Stack spacing={1} alignItems="center">
                <Box
                  component="a"
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ lineHeight: 0 }}
                >
                  <Box
                    component="img"
                    src="/zeta_playstore.jpg"
                    alt="Get it on Google Play"
                    sx={{
                      width: 130,
                      height: 130,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.background.paper, 0.98),
                      p: 1,
                      border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.3 : 1)}`,
                      boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.25)' : '0 8px 24px rgba(15,23,42,0.08)',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.75)' : theme.palette.text.secondary,
                  }}
                >
                  Google Play
                </Typography>
              </Stack>
            </Stack>

            {/* Support */}
            <Stack spacing={0.5} alignItems="center">
              <Typography
                sx={{
                  fontSize: 13,
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                }}
              >
                Need help accessing your account?
              </Typography>

              <Typography
                component="a"
                href="mailto:support@zetawealth.co.uk"
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.palette.primary.light,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                support@zetawealth.co.uk
              </Typography>
            </Stack>
          </Stack>

        </Box>
      </Box>

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        minWidth: { lg: NAV.W_VERTICAL },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          slotProps={{
            paper: {
              sx: {
                width: NAV.W_VERTICAL,
              },
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
